import { useEffect, useState } from 'react';
import { NAV } from '../nav';

/**
 * Left navigation with scroll spy.
 *
 * Uses IntersectionObserver rather than scroll maths: it fires only when a
 * section actually crosses the band, so there is no listener running on every
 * frame. The band sits in the upper third of the viewport so the highlighted
 * item matches what you are reading, not what is technically topmost.
 */
export function Sidebar() {
  const [active, setActive] = useState<string>(NAV[0].id);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const ids = NAV.flatMap((item) => [item.id, ...(item.children ?? []).map((c) => c.id)]);
    const targets = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isActive = (id: string, children?: Array<{ id: string }>) =>
    active === id || (children ?? []).some((c) => c.id === active);

  return (
    <>
      <button
        type="button"
        className="nav-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '✕' : '☰'} Contents
      </button>

      <nav className={`sidebar${open ? ' is-open' : ''}`} aria-label="Page sections">
        <a className="brand" href="#overview" onClick={() => setOpen(false)}>
          Fargate Deployer
        </a>

        <ul>
          {NAV.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={isActive(item.id, item.children) ? 'active' : undefined}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </a>
              {item.children && isActive(item.id, item.children) && (
                <ul className="sub">
                  {item.children.map((child) => (
                    <li key={child.id}>
                      <a
                        href={`#${child.id}`}
                        className={active === child.id ? 'active' : undefined}
                        onClick={() => setOpen(false)}
                      >
                        {child.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        <div className="sidebar-foot">
          <a href="https://github.com/FutureMoney/fargate-deployer">GitHub ↗</a>
          <a href="https://www.npmjs.com/package/fargate-deployer">npm ↗</a>
          <a href="https://github.com/marketplace/actions/fargate-deployer">Marketplace ↗</a>
        </div>
      </nav>
    </>
  );
}
